import { Component, OnInit, Inject, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { ReportService, ReportData, ReportParams } from '../../core/services/report.service';
import { SubordinatesService, SubordinateEmployee } from '../../core/services/subordinates.service';
import { DateHolidayService } from '../../core/services/date-holiday.service';
import { LoginService } from '../../core/services/login.service';
import { CHART_COLOR_PALETTE } from '../../shared/constants/chart-palette';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';

interface ChartData {
  name: string;
  value: number;
}

@Component({
  selector: 'app-ng-reports',
  template: require('./ng-reports.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-reports.component.scss').default],
  standalone: false,
})
export class NgReportsComponent implements OnInit {
  // Form data
  dateFrom: moment.Moment | null = null;
  dateTo: moment.Moment | null = null;

  // Filters and modes
  selectedEmployees: SubordinateEmployee[] = [];
  groupMode: 'KU' | 'P' | 'A' = 'A';
  custMode = { external: true, internal: true };
  details = false;

  // Data
  invoice: ReportData[] = [];
  exportData: any[][] = [];
  header: string[] = ['Konsult', 'Kund', 'Projekt', 'Aktivitet', 'Timmar'];
  subordinates: SubordinateEmployee[] = [];
  periodHours: { [month: number]: number } = {};

  // Chart data
  salesReport: ChartData[] = [];
  colorScheme = {
    domain: [...CHART_COLOR_PALETTE],
  };

  // Computed property for total hours
  get totalHours(): number {
    return this.invoice.reduce((sum, item) => {
      return sum + (typeof item.sum === 'number' ? item.sum : 0);
    }, 0);
  }

  // Computed property for total work hours
  get totalWorkHours(): number {
    return Object.values(this.periodHours).reduce((sum, hours) => sum + hours, 0);
  }

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(ReportService) private reportService: ReportService,
    @Inject(SubordinatesService) private subordinatesService: SubordinatesService,
    @Inject(DateHolidayService) private dateHolidayService: DateHolidayService,
    @Inject(LoginService) private loginService: LoginService
  ) {}

  ngOnInit(): void {
    this.initializeDates();
    this.loadSubordinates();
  }

  private initializeDates(): void {
    // Set initial date range based on current date
    if (moment().date() > 15) {
      this.dateFrom = moment().startOf('month');
    } else {
      this.dateFrom = moment().subtract(1, 'month').startOf('month');
    }
    this.dateTo = moment(this.dateFrom).add(1, 'month').subtract(1, 'days');
  }

  private loadSubordinates(): void {
    const isChef = this.loginService.arChef() || this.loginService.arAdmin();
    if (isChef) {
      this.subordinatesService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (data) => {
          this.subordinates = data;
        },
        error: (error) => {
          console.error('Error loading subordinates:', error);
        },
      });
    }
  }

  updatePeriod(direction: number): void {
    if (!this.dateFrom || !this.dateTo) return;

    if (direction === -1) {
      this.dateFrom = moment(this.dateFrom).subtract(1, 'months').startOf('month');
      this.dateTo = moment(this.dateFrom).endOf('month');
    } else {
      this.dateFrom = moment(this.dateFrom).add(1, 'months').startOf('month');
      this.dateTo = moment(this.dateFrom).endOf('month');
    }
  }

  calculateWorkHours(): void {
    if (!this.dateFrom || !this.dateTo) return;
    this.periodHours = this.dateHolidayService.getWorkHoursByMonthBetween(
      this.dateFrom.clone(),
      this.dateTo.clone()
    );
  }

  fetchInvoice(): void {
    if (!this.dateFrom || !this.dateTo) return;

    this.invoice = [];
    this.exportData = [];

    // Set headers based on grouping mode
    switch (this.groupMode) {
      case 'A':
        this.header = ['Kund', 'Projekt', 'Aktivitet', 'Timmar'];
        break;
      case 'P':
        this.header = ['Kund', 'Projekt', 'Timmar'];
        break;
      case 'KU':
        this.header = ['Kund', 'Timmar'];
        break;
    }

    if (this.details) {
      this.header.unshift('Datum');
    }
    this.header.unshift('Konsult');

    // Build params
    const ids = this.selectedEmployees.map((emp) => emp.employee_id);
    const params: ReportParams = {
      id: ids.length > 0 ? ids : undefined,
      details: this.details,
      custMode: this.custMode,
      per: this.groupMode, // per = timeMode in original (groupMode is correct)
      sum: this.groupMode, // sum = sumMode in original (groupMode is correct)
      type: this.groupMode,
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
    };

    this.reportService.get(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.processInvoiceData(data);
        // Automatically calculate work hours when fetching invoice
        this.calculateWorkHours();
      },
      error: (error) => {
        console.error('Error fetching report:', error);
      },
    });
  }

  private processInvoiceData(data: ReportData[]): void {
    this.invoice = data.map((item) => {
      const processed = { ...item };

      // Store numeric values
      processed.sumNum = item.sum;
      //processed.prisNum = item.pris;

      // Format for display
      try {
        processed.sum = item.sum as any;
      } catch (error) {
        processed.sum = 0 as any;
      }

      // Handle null customer names
      if (!processed.customername) {
        processed.customername = 'Tot';
      }

      return processed;
    });

    // Build chart data
    this.buildChartData();
  }

  private buildChartData(): void {
    this.salesReport = [];

    // Aggregate data by key (customer/project/activity)
    const aggregatedData = new Map<string, number>();

    this.invoice.forEach((item) => {
      let key: string | null = null;

      switch (this.groupMode) {
        case 'KU':
          if (item.customername && item.customername !== 'Tot') {
            key = item.customername;
          }
          break;
        case 'P':
          if (item.project_name) {
            key = item.project_name;
          }
          break;
        case 'A':
          if (item.activity_name) {
            key = item.activity_name;
          }
          break;
      }

      if (key && item.sumNum !== undefined) {
        const currentSum = aggregatedData.get(key) || 0;
        aggregatedData.set(key, currentSum + item.sumNum);
      }
    });

    // Convert aggregated data to chart format
    aggregatedData.forEach((value, name) => {
      this.salesReport.push({ name, value });
    });
  }

  getCellValue(head: string, inv: ReportData, index: number, row: number): any {
    if (!this.exportData[row]) {
      this.exportData[row] = [];
    }

    let value: any;

    switch (head) {
      case 'Konsult':
        value = inv.name;
        break;
      case 'Kund':
        value = inv.customername;
        break;
      case 'Projekt':
        value = inv.project_name;
        break;
      case 'Aktivitet':
        value = inv.activity_name;
        break;
      case 'Timmar':
        value = typeof inv.sum === 'number' ? inv.sum : 0;
        break;
      case 'Datum':
        value = inv.reporteddate ? moment(inv.reporteddate).format('YYYY-MM-DD') : '';
        break;
      default:
        value = '';
    }

    this.exportData[row][index] = value;
    return value;
  }

  isNumericHeader(head: string): boolean {
    return head === 'Timmar';
  }

  exportToExcel(): void {
    if (this.exportData.length === 0) {
      return;
    }

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add header row
    const wsData = [this.header, ...this.exportData];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    removeBlankStringCells(ws);

    // Apply cell formatting
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

    // Find column indices for Date and Timmar
    const datumColIndex = this.header.indexOf('Datum');
    const timmarColIndex = this.header.indexOf('Timmar');

    // Format cells (skip header row, start from row 1)
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      // Format Datum column as date (YYYY-MM-DD)
      if (datumColIndex !== -1) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: datumColIndex });
        const cell = ws[cellAddress];
        if (cell && cell.v) {
          // Parse YYYY-MM-DD string to Excel date
          const dateParts = String(cell.v).split('-');
          if (dateParts.length === 3) {
            const date = new Date(
              parseInt(dateParts[0]),
              parseInt(dateParts[1]) - 1,
              parseInt(dateParts[2])
            );
            cell.t = 'd';
            cell.v = date;
            cell.z = 'yyyy-mm-dd';
          }
        }
      }

      // Format Timmar column as number
      if (timmarColIndex !== -1) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: timmarColIndex });
        const cell = ws[cellAddress];
        if (cell && cell.v) {
          const numValue = typeof cell.v === 'number' ? cell.v : Number(cell.v);
          if (!isNaN(numValue)) {
            cell.t = 'n';
            cell.v = numValue;
            // Use decimal format only if number has decimals
            cell.z = numValue % 1 === 0 ? '0' : '0.00';
          }
        }
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Tidrapport');

    // Save file as Excel (.xlsx format)
    XLSX.writeFile(wb, 'tidRapport.xlsx', { bookType: 'xlsx' });
  }

  // Chart event handlers (optional)
  onSelect(event: any): void {
    console.log('Chart item selected:', event);
  }

  onActivate(event: any): void {
    // Handle chart activation if needed
  }

  onDeactivate(event: any): void {
    // Handle chart deactivation if needed
  }
}
